import { api, API } from "@/lib/api";
import { authHeader } from "@/lib/aadhaar";
import { isStudent } from "@/lib/classroom_live";

const opt = (params) => ({ ...(isStudent() ? authHeader("student") : {}), params });

export const AIOT_PATH = "/courses/aiot-diploma";
export const PORTAL = `${AIOT_PATH}/learn`;
export const SIMRAN = ["/aiot/simran-1.jpg", "/aiot/simran-2.jpg"];
export const WHATSAPP = `https://wa.me/?text=${encodeURIComponent("Hello RoboSpark, I would like to know more about the Advanced AIoT Diploma Course.")}`;
export const loginUrl = (next) => `/student/login?next=${encodeURIComponent(next)}`;

export const fetchCourse = () => api.get("/aiot/course", opt()).then((r) => r.data);
export const fetchLecture = (id, lang) => api.get(`/aiot/lecture/${id}`, opt({ lang })).then((r) => r.data);
export const fetchVoice = (id, n, lang) => api.get(`/aiot/tts/${id}/${n}`, opt({ lang })).then((r) => r.data);
export const applyAiot = (body) => api.post("/aiot/apply", body).then((r) => r.data);
export const fetchMe = () => api.get("/aiot/me", authHeader("student")).then((r) => r.data);
export const markDone = (id) => api.post(`/aiot/progress/${id}`, {}, authHeader("student")).catch(() => {});

export const fetchLabs = () => api.get("/aiot/labs", authHeader("student")).then((r) => r.data);
export const fetchLab = (id) => api.get(`/aiot/labs/${id}`, opt()).then((r) => r.data);
export const runLab = (id, body) => api.post(`/aiot/labs/${id}/run`, body, opt()).then((r) => r.data);
export const LAB_PATH = (id) => `${AIOT_PATH}/lab/${id}`;

export const testApi = {
  status: (w) => api.get(`/aiot/tests/${w}`, authHeader("student")).then((r) => r.data),
  start: (w) => api.post(`/aiot/tests/${w}/start`, {}, authHeader("student")).then((r) => r.data),
  save: (w, answers) => api.post(`/aiot/tests/${w}/save`, { answers }, authHeader("student")).catch(() => {}),
  event: (w, type) => api.post(`/aiot/tests/${w}/event`, { type }, authHeader("student")).then((r) => r.data),
  submit: (w, answers) => api.post(`/aiot/tests/${w}/submit`, { answers }, authHeader("student")).then((r) => r.data),
};

export const adminAiot = (auth) => ({
  apps: () => api.get("/admin/aiot/applications", auth).then((r) => r.data.items),
  decide: (id, action) => api.post(`/admin/aiot/applications/${id}/${action}`, {}, auth).then((r) => r.data),
  students: () => api.get("/admin/aiot/students", auth).then((r) => r.data.items),
  capstone: (uid, body) => api.post(`/admin/aiot/students/${uid}/capstone`, body, auth),
  classes: () => api.get("/admin/aiot/classes", auth).then((r) => r.data.items),
  media: (cid, body) => api.put(`/admin/aiot/classes/${cid}`, body, auth),
  attendance: () => api.get("/admin/aiot/attendance", auth).then((r) => r.data.items),
});

// Attendance ping when an enrolled student clicks Join; never blocks opening the link.
export const markLiveJoin = (cid) => {
  const t = localStorage.getItem("rs_student_token");
  if (t) fetch(`${API}/aiot/live/${cid}/join`, { method: "POST", keepalive: true, headers: { Authorization: `Bearer ${t}` } }).catch(() => {});
};

export const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return fallback;
};

// Spots in the 3D flat used by the "place the device" test tasks.
export const SPOTS = {
  door_top: { en: "Above the main door", hi: "मुख्य दरवाज़े के ऊपर", p: [-3.9, 2.45, 0.9] },
  door_frame: { en: "On the door frame", hi: "दरवाज़े की चौखट पर", p: [-3.9, 1.15, 1.55] },
  window_frame: { en: "On the window frame", hi: "खिड़की की फ़्रेम पर", p: [-0.25, 1.6, -2.92] },
  window_sill: { en: "On the window sill", hi: "खिड़की की सिल पर", p: [-1.2, 0.98, -2.8] },
  wall_inner: { en: "Inner wall, away from sun and vents", hi: "अंदरूनी दीवार, धूप और वेंट से दूर", p: [1.52, 1.5, -0.9] },
  shelf_center: { en: "Central shelf in the living room", hi: "लिविंग रूम का बीच वाला शेल्फ़", p: [0.35, 1.95, -2.8] },
  ceiling_center: { en: "Centre of the ceiling", hi: "छत के बीच में", p: [-1, 2.95, -0.3] },
  corner_high: { en: "High corner of the room", hi: "कमरे का ऊँचा कोना", p: [-3.85, 2.85, -2.85] },
  kitchen_low: { en: "Kitchen, low near the floor", hi: "किचन में, फ़र्श के पास नीचे", p: [3.35, 0.3, -2.3] },
  under_sink: { en: "Under the kitchen sink", hi: "किचन सिंक के नीचे", p: [2.4, 0.22, -2.3] },
};

export const RULE_HI = {
  motion: "मोशन दिखा", no_motion_10: "10 मिनट से कोई मोशन नहीं", temp_28: "तापमान 28 °C से ऊपर", smoke: "धुआँ मिला", gas: "LPG गैस मिली", leak: "पानी का लीक मिला",
  door_open: "मुख्य दरवाज़ा खुला", lux_low: "रोशनी 50 lux से कम", time_2300: "घड़ी में रात 11 बजे", card_in: "होटल की-कार्ड डाला गया", card_out: "होटल की-कार्ड निकाला गया",
  always: "हमेशा (कोई शर्त नहीं)", dark: "बाहर अँधेरा है", empty: "कमरे में कोई नहीं है", ac_on: "AC चल रहा है", armed: "अलार्म आर्म्ड है", occupied: "कमरे में कोई है", weekday: "आज कार्यदिवस है",
  lights_on: "लाइट ON करें", lights_off: "लाइट OFF करें", ac_off: "AC OFF करें", ac_cool: "AC को 24 °C पर कूल करें", siren: "सायरन बजाएँ और परिवार को अलर्ट करें",
  gas_valve: "गैस वाल्व बंद करें और अलर्ट करें", water_valve: "पानी का वाल्व बंद करें और अलर्ट करें", lock_all: "सारे दरवाज़े लॉक करें", welcome: "वेलकम सीन: लाइट और AC ON", eco: "इको सीन: लाइट और AC OFF",
  shift_start: "सुबह 8 बजे शिफ़्ट शुरू", curtain_break: "लाइट कर्टेन की बीम टूटी", estop_press: "इमरजेंसी स्टॉप दबाया गया", motor_hot: "मोटर का तापमान 80 °C से ऊपर",
  vib_high: "वाइब्रेशन सामान्य सीमा से ऊपर", no_count_30: "30 सेकंड से कोई बोतल नहीं गिनी गई", shift_end: "शाम 4 बजे शिफ़्ट ख़त्म", running: "कन्वेयर चल रहा है",
  stopped: "कन्वेयर रुका है", operator: "ऑपरेटर लाइन पर है", maint: "मेंटेनेंस मोड ON है", start_line: "कन्वेयर शुरू करें, स्टैक लाइट हरी",
  stop_line: "कन्वेयर रोकें, स्टैक लाइट लाल, बज़र बजाएँ", warn: "स्टैक लाइट पीली, मेंटेनेंस को अलर्ट", slow: "कन्वेयर 50% धीमा करें",
  shutdown: "शिफ़्ट ख़त्म शटडाउन: कन्वेयर और लाइट बंद", buzz: "सिर्फ़ बज़र बजाएँ",
};

export const LABS_LIST = [["home", "Smart Home Automation Lab", "स्मार्ट होम ऑटोमेशन लैब"], ["hotel", "Hotel Room Automation Lab", "होटल रूम ऑटोमेशन लैब"], ["factory", "Smart Factory Line Lab", "स्मार्ट फ़ैक्ट्री लाइन लैब"]];
export const verifyLabCert = (no) => api.get(`/aiot/certificate/verify/${encodeURIComponent(no)}`).then((r) => r.data);
export const certUrl = (no) => `${window.location.origin}/verify-aiot/${no}`;
